import React, { useState } from 'react';
import * as monaco from 'monaco-editor';
import MonacoEditorWithFullscreen from 'components/Common/MonacoEditorWithFullscreen';
import { useTheme } from 'contexts/ThemeContext';

// Define custom theme for the editor
monaco.editor.defineTheme("response-theme", {
    base: 'vs',
    inherit: true,
    rules: [
        // Status code colors
        { token: 'status-2xx', foreground: '047857' },      // text-green-800
        { token: 'status-3xx', foreground: '92400E' },      // text-amber-800
        { token: 'status-4xx', foreground: '991B1B' },      // text-red-800
        { token: 'status-5xx', foreground: '991B1B' },      // text-red-800
        // Other syntax elements
        { token: 'header', foreground: 'A31515' },          // Red for headers
        { token: 'value', foreground: '0451A5' },           // Blue for values
        { token: 'delimiter', foreground: 'A31515' },       // Red for brackets
        { token: 'key', foreground: 'A31515' },             // Red for JSON keys
        { token: 'section', foreground: '6B7280' },         // Gray for section headers
        { token: 'label', foreground: '4B5563' },           // Dark gray for labels
    ],
    colors: {
        'editor.background': '#ffffff',             // Light mode background
        'editor.foreground': '#282a2c',             // Light mode text
        'editorLineNumber.foreground': '#64748B',   // Light mode line numbers
        'editor.lineHighlightBackground': 'rgba(0, 0, 0, 0.04)',
        'editor.lineHighlightBorder': 'rgba(0, 0, 0, 0.04)',
        'editorGutter.background': '#e9e9e9',       // Light mode gutter
    }
});

// Define dark theme for the editor
monaco.editor.defineTheme("response-theme-dark", {
    base: 'vs-dark',
    inherit: true,
    rules: [
        // Status code colors - brighter for dark mode
        { token: 'status-2xx', foreground: '4ade80' },      // brighter green
        { token: 'status-3xx', foreground: 'fbbf24' },      // brighter amber
        { token: 'status-4xx', foreground: 'f87171' },      // brighter red
        { token: 'status-5xx', foreground: 'f87171' },      // brighter red
        // Other syntax elements - brighter for dark mode
        { token: 'header', foreground: 'f87171' },          // brighter red for headers
        { token: 'value', foreground: '60a5fa' },           // brighter blue for values
        { token: 'delimiter', foreground: 'f87171' },       // brighter red for brackets
        { token: 'key', foreground: 'f87171' },             // brighter red for JSON keys
        { token: 'section', foreground: '94a3b8' },         // brighter gray for section headers
        { token: 'label', foreground: 'cbd5e1' },           // brighter gray for labels
    ],
    colors: {
        'editor.background': '#0e1525',             // Your dark theme background
        'editor.foreground': '#d4d4d4',             // VS Code light gray text
        'editorLineNumber.foreground': '#94a3b8',   // Your dark theme line numbers
        'editor.lineHighlightBackground': 'rgba(255, 255, 255, 0.04)',
        'editor.lineHighlightBorder': 'rgba(255, 255, 255, 0.04)',
        'editorGutter.background': '#0e1525',       // Your dark theme gutter
        'editor.selectionBackground': '#264f78',     // VS Code selection
        'editor.inactiveSelectionBackground': '#3a3d41', // VS Code inactive selection
        'editor.wordHighlightBackground': '#575757', // VS Code word highlight
        'editor.findMatchBackground': '#515c6a',     // VS Code find match
        'editor.findMatchHighlightBackground': '#515c6a', // VS Code find match highlight
    }
});

// Register a custom language for response highlighting
monaco.languages.register({ id: 'response-language' });
monaco.languages.setMonarchTokensProvider('response-language', {
    tokenizer: {
        root: [
            // Status codes with specific colors
            [/HTTP\/1\.1\s+2\d{2}/, 'status-2xx'],
            [/HTTP\/1\.1\s+3\d{2}/, 'status-3xx'],
            [/HTTP\/1\.1\s+4\d{2}/, 'status-4xx'],
            [/HTTP\/1\.1\s+5\d{2}/, 'status-5xx'],
            // Headers
            [/^[A-Za-z-]+:/, 'header'],
            // Header values
            [/:(.*)$/, 'value'],
            // JSON keys
            [/"([^"]*)"(?=\s*:)/, 'key'],
            // Brackets and braces
            [/[{}\[\],]/, 'delimiter'],
            // Section headers
            [/^[A-Z][a-z]+( [A-Z][a-z]+)*:$/, 'section'],
            // Labels
            [/^[A-Za-z-]+:/, 'label'],
        ]
    }
});

const ResponseDetails = ({ requestData }) => {
    const { isDarkMode } = useTheme();

    if (!requestData?.response) {
        return (
            <div className="space-y-4">
                <div className="bgPrimary rounded-lg border !border-[var(--borderColor)] shadow-sm hover:shadow-md transition-shadow duration-200">
                    <div className="px-4 py-2 flex justify-between items-center" style={{ borderBottom: '2px solid var(--borderColor)' }}>
                        <span className="!text-xl text-[var(--text-secondary)]">Response</span>
                    </div>
                    <div className="h-[calc(100vh-310px)] flex items-center justify-center">
                        <p className="text-[var(--text-secondary)] text-lg">No Response Available</p>
                    </div>
                </div>
            </div>
        );
    }

    const formatResponse = () => {
        const lines = [];

        // Request Information
        lines.push('Request Information:');
        lines.push(`Method: ${requestData.response.request_type}`);
        lines.push(`URL: ${requestData.response.request_url}`);
        if (requestData.response.request_headers?.length > 0) {
            lines.push('Request Headers:');
            requestData.response.request_headers.forEach(header => {
                lines.push(`${header.name}: ${header.value}`);
            });
        }
        if (requestData.response.request_body) {
            lines.push('Request Body:');
            try {
                const formattedBody = JSON.stringify(requestData.response.request_body, null, 2);
                lines.push(formattedBody);
            } catch (e) {
                lines.push(requestData.response.request_body);
            }
        }
        lines.push('');

        // Response Information
        lines.push('Response Information:');
        lines.push(`Status: ${requestData.response.status}`);
        lines.push(`Time: ${requestData.response.time}ms`);
        lines.push(`Size: ${requestData.response.size || 'N/A'}`);
        lines.push(`Created: ${new Date(requestData.response.created_at).toLocaleString()}`);
        lines.push('');

        // Response Headers
        lines.push('Response Headers:');
        if (requestData.response.headers) {
            Object.entries(requestData.response.headers).forEach(([name, value]) => {
                lines.push(`${name}: ${value}`);
            });
        }
        lines.push('');

        // Response Body
        lines.push('Response Body:');
        if (requestData.response.body) {
            try {
                const formattedBody = JSON.stringify(requestData.response.body, null, 2);
                lines.push(formattedBody);
            } catch (e) {
                lines.push(requestData.response.body);
            }
        }

        // Test Results
        if (requestData.response.total_tests > 0) {
            lines.push('');
            lines.push('Test Results:');
            lines.push(`Total Tests: ${requestData.response.total_tests}`);
            lines.push(`Passed: ${requestData.response.tests_passed}`);
            lines.push(`Failed: ${requestData.response.tests_failed}`);
        }

        return lines.join('\n');
    };

    return (
        <div className="space-y-4">
            <div className="bgPrimary rounded-lg border !border-[var(--borderColor)] shadow-sm hover:shadow-md transition-shadow duration-200">
                <div className="px-4 py-2 flex justify-between items-center" style={{ borderBottom: '2px solid var(--borderColor)' }}>
                    <span className="!text-xl text-[var(--text-secondary)]">Response</span>
                </div>
                <MonacoEditorWithFullscreen
                    language="response-language"
                    theme={isDarkMode ? "response-theme-dark" : "response-theme"}
                    value={formatResponse()}
                    height="calc(100vh - 300px)"
                    options={{
                        readOnly: true,
                        minimap: { enabled: false },
                        lineNumbers: 'on',
                        renderLineHighlight: 'none',
                        scrollBeyondLastLine: false,
                        fontSize: 12,
                        fontFamily: 'Monaco, monospace',
                        wordWrap: 'on',
                        automaticLayout: true,
                        scrollbar: {
                            vertical: 'visible',
                            horizontal: 'visible',
                            verticalScrollbarSize: 14,
                            horizontalScrollbarSize: 14,
                            verticalSliderSize: 14,
                            horizontalSliderSize: 14,
                        },
                        overviewRulerBorder: false,
                        hideCursorInOverviewRuler: true,
                    }}
                />
            </div>
        </div>
    );
};

export default ResponseDetails;